import { useState } from 'react'
import {
  useChangeIntranetMarketItem,
  useCreateIntranetKudos,
  useCreateIntranetMarketItem,
  useDeleteIntranetKudos,
  useDeleteIntranetTip,
  useIntranetKudos,
  useIntranetMarket,
  useIntranetTips,
  useMarkIntranetTipRead,
  useSaveIntranetTip,
  useUsers
} from '../api/queries'
import type { IntranetMarketItem, IntranetMarketKind, IntranetTip, UserRef } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { useCreateRequest } from '../lib/createRequest'
import { prepareDocumentFile } from '../lib/documentPhoto'
import { formatDate, formatDateTime } from '../lib/format'
import { useAuthedBlob } from '../lib/photos'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from './ui'

// Ros, køb/salg og ugens faglige tip på intranettet (docs/DECISIONS.md beslutning 121).

const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

function Photo({ url, size = 88 }: { url: string; size?: number }) {
  const blob = useAuthedBlob(url)
  if (!blob) return <div style={{ width: size, height: size, borderRadius: 6, background: '#F1F5F9' }} />
  return (
    <a href={blob} target="_blank" rel="noreferrer">
      <img src={blob} alt="" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 6 }} />
    </a>
  )
}

// --- Ros ---

/** Denne måned i dansk tid som yyyy-MM. */
function thisMonth() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit' })
    .formatToParts(new Date())
  return `${parts.find((p) => p.type === 'year')!.value}-${parts.find((p) => p.type === 'month')!.value}`
}

function shiftMonth(month: string, by: number) {
  const [year, number] = month.split('-').map(Number)
  const date = new Date(Date.UTC(year, number - 1 + by, 1))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

function monthLabel(month: string) {
  const [year, number] = month.split('-').map(Number)
  const text = new Intl.DateTimeFormat('da-DK', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, number - 1, 15))
  )
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Fanen "Ros": en væg med rosen og en oversigt over måneden. Alle kan rose. */
export function KudosTab() {
  const [month, setMonth] = useState(thisMonth)
  const { data, isLoading, error } = useIntranetKudos(month)
  const remove = useDeleteIntranetKudos()
  const [writing, setWriting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  useCreateRequest(['ros'], () => {
    setNotice(null)
    setWriting(true)
  })

  const current = thisMonth()

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div className="small muted">Har en kollega gjort noget godt? Sig det, så alle kan se det.</div>
        {!writing && (
          <button className="btn btn--primary" onClick={() => { setNotice(null); setWriting(true) }}>
            🙌 Ros en kollega
          </button>
        )}
      </div>

      {notice && <div className="map-notice">{notice}</div>}
      {writing && (
        <KudosForm
          onClose={(message) => {
            setWriting(false)
            if (message) setNotice(message)
            setMonth(current)
          }}
        />
      )}

      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <button className="btn btn--sm" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="Forrige måned">‹</button>
        <strong style={{ minWidth: 150, textAlign: 'center' }}>{monthLabel(month)}</strong>
        <button className="btn btn--sm" disabled={month >= current} onClick={() => setMonth(shiftMonth(month, 1))} aria-label="Næste måned">›</button>
      </div>

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data && data.top.length > 0 && (
        <Card title={`🏆 Mest rost i ${monthLabel(month).toLowerCase()}`}>
          <div className="stack" style={{ gap: 6 }}>
            {data.top.slice(0, 5).map((row, index) => (
              <div key={row.user.id} className="row" style={{ gap: 10 }}>
                <span className="tiny muted" style={{ width: 16 }}>{index + 1}.</span>
                <Avatar user={row.user} size={28} />
                <span className="small" style={{ flex: 1 }}>{row.user.displayName}</span>
                <span className="pill">{row.count} {row.count === 1 ? 'ros' : 'gange'}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data && data.items.length === 0 && <EmptyState>Ingen ros i {monthLabel(month).toLowerCase()} endnu.</EmptyState>}

      {data?.items.map((kudos) => (
        <Card key={kudos.id}>
          <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
            <div className="row" style={{ gap: 0 }}>
              {kudos.recipients.slice(0, 3).map((user) => (
                <Avatar key={user.id} user={user} size={40} />
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="small">
                🙌 <strong>{kudos.recipients.map((r) => r.displayName).join(', ')}</strong>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{kudos.message}</div>
              <div className="tiny muted" style={{ marginTop: 4 }}>
                Fra {kudos.from?.displayName ?? 'en tidligere kollega'} · {formatDateTime(kudos.createdAt)}
              </div>
            </div>
            {kudos.canDelete && (
              <button
                className="btn btn--sm"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm('Slet rosen?')) remove.mutate(kudos.id)
                }}
              >
                Slet
              </button>
            )}
          </div>
        </Card>
      ))}
    </div>
  )
}

function KudosForm({ onClose }: { onClose: (message?: string) => void }) {
  const { user: me } = useAuth()
  const { data: users } = useUsers()
  const create = useCreateIntranetKudos()
  const [chosen, setChosen] = useState<UserRef[]>([])
  const [message, setMessage] = useState('')
  const [error, setError] = useState<string | null>(null)

  const candidates = (users ?? []).filter((u) => u.isActive && u.id !== me?.id && !chosen.some((c) => c.id === u.id))

  async function send() {
    setError(null)
    try {
      await create.mutateAsync({ recipientIds: chosen.map((u) => u.id), message: message.trim() })
      onClose(`Tak! ${chosen.map((u) => u.displayName).join(' og ')} har fået besked.`)
    } catch (err) {
      setError(errorText(err, 'Rosen kunne ikke gemmes.'))
    }
  }

  return (
    <Card title="🙌 Ros en kollega">
      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {chosen.map((user) => (
            <button
              key={user.id}
              type="button"
              className="btn btn--sm"
              onClick={() => setChosen(chosen.filter((c) => c.id !== user.id))}
              title="Fjern"
            >
              {user.displayName} ✕
            </button>
          ))}
          <select
            value=""
            aria-label="Vælg en kollega"
            onChange={(e) => {
              const user = candidates.find((u) => u.id === Number(e.target.value))
              if (user) setChosen([...chosen, user])
            }}
          >
            <option value="">{chosen.length === 0 ? 'Hvem vil du rose?' : '+ En til ...'}</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>{u.displayName}</option>
            ))}
          </select>
        </div>
        <label className="field">
          <span className="field__label">Hvad vil du rose for? *</span>
          <textarea rows={3} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="F.eks. tak for hjælpen med tavlen på Vesterbro i går" />
        </label>
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={chosen.length === 0 || !message.trim() || create.isPending} onClick={() => void send()}>
            {create.isPending ? 'Sender ...' : 'Send rosen'}
          </button>
          <button className="btn" onClick={() => onClose()}>Annullér</button>
        </div>
      </div>
    </Card>
  )
}

// --- Køb, salg og bytte ---

const MARKET_KINDS: { key: IntranetMarketKind; label: string; color: string }[] = [
  { key: 'sell', label: 'Sælges', color: '#2563EB' },
  { key: 'wanted', label: 'Købes', color: '#7C3AED' },
  { key: 'free', label: 'Gives væk', color: '#16A34A' },
  { key: 'lend', label: 'Lånes / byttes', color: '#D97706' }
]

/** Fanen "Køb og salg": en opslagstavle for de ansatte. Et opslag står i 30 dage. */
export function MarketTab() {
  const { data, isLoading, error } = useIntranetMarket()
  const [filter, setFilter] = useState<IntranetMarketKind | 'all'>('all')
  const [writing, setWriting] = useState(false)

  useCreateRequest(['salg'], () => setWriting(true))

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const open = (data ?? []).filter((i) => i.isOpen && (filter === 'all' || i.kind === filter))
  const mineClosed = (data ?? []).filter((i) => i.isMine && !i.isOpen)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <button className={`btn btn--sm ${filter === 'all' ? 'btn--primary' : ''}`} onClick={() => setFilter('all')}>Alle</button>
          {MARKET_KINDS.map((k) => (
            <button key={k.key} className={`btn btn--sm ${filter === k.key ? 'btn--primary' : ''}`} onClick={() => setFilter(k.key)}>
              {k.label}
            </button>
          ))}
        </div>
        {!writing && <button className="btn btn--primary" onClick={() => setWriting(true)}>Nyt opslag</button>}
      </div>

      {writing && <MarketForm onClose={() => setWriting(false)} />}

      {open.length === 0 && <EmptyState>Ingen opslag lige nu.</EmptyState>}
      {open.map((item) => <MarketCard key={item.id} item={item} />)}

      {mineClosed.length > 0 && (
        <>
          <div className="small muted" style={{ marginTop: 8 }}>Dine afsluttede og udløbne opslag</div>
          {mineClosed.map((item) => <MarketCard key={item.id} item={item} />)}
        </>
      )}
    </div>
  )
}

function MarketCard({ item }: { item: IntranetMarketItem }) {
  const change = useChangeIntranetMarketItem()
  const [error, setError] = useState<string | null>(null)
  const kind = MARKET_KINDS.find((k) => k.key === item.kind)

  async function act(action: 'close' | 'renew' | 'delete') {
    if (action === 'delete' && !window.confirm('Slet opslaget?')) return
    setError(null)
    try {
      await change.mutateAsync({ id: item.id, action })
    } catch (err) {
      setError(errorText(err, 'Opslaget kunne ikke ændres.'))
    }
  }

  return (
    <Card>
      <div className="stack" style={{ gap: 8, opacity: item.isOpen ? 1 : 0.65 }}>
        <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="row" style={{ gap: 8, minWidth: 0 }}>
            <span className="pill" style={{ background: kind?.color ?? '#64748B', color: '#fff' }}>{item.kindName}</span>
            <strong>{item.title}</strong>
          </div>
          {item.price && <strong>{item.price}</strong>}
        </div>
        {item.description && <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{item.description}</div>}
        {item.photoUrls.length > 0 && (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {item.photoUrls.map((url) => <Photo key={url} url={url} />)}
          </div>
        )}
        <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="row" style={{ gap: 8 }}>
            <Avatar user={item.user} size={26} />
            <span className="tiny muted">
              {item.user.displayName} ·{' '}
              {item.closedAt ? 'Afsluttet' : item.isOpen ? `står til ${formatDate(item.expiresAt)}` : 'Udløbet'}
            </span>
          </div>
          {!item.isMine && item.user.phoneNumber && item.isOpen && (
            <a className="btn btn--sm" href={`tel:${item.user.phoneNumber.replace(/\s/g, '')}`}>📞 Ring</a>
          )}
        </div>
        {item.canManage && (
          <div className="btn-group">
            {item.isOpen && (
              <button className="btn btn--sm" disabled={change.isPending} onClick={() => void act('close')}>
                {item.kind === 'sell' ? 'Solgt' : 'Afslut'}
              </button>
            )}
            {item.isMine && !item.closedAt && (
              <button className="btn btn--sm" disabled={change.isPending} onClick={() => void act('renew')}>Forlæng 30 dage</button>
            )}
            <button className="btn btn--sm" disabled={change.isPending} onClick={() => void act('delete')}>Slet</button>
          </div>
        )}
        {error && <div className="error-box">{error}</div>}
      </div>
    </Card>
  )
}

function MarketForm({ onClose }: { onClose: () => void }) {
  const create = useCreateIntranetMarketItem()
  const [kind, setKind] = useState<IntranetMarketKind>('sell')
  const [title, setTitle] = useState('')
  const [price, setPrice] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  async function send() {
    setError(null)
    try {
      const photos = []
      for (const file of files.slice(0, 3)) photos.push(await prepareDocumentFile(file))
      await create.mutateAsync({ kind, title: title.trim(), price: price.trim(), description: description.trim(), photos })
      onClose()
    } catch (err) {
      setError(errorText(err, 'Opslaget kunne ikke gemmes.'))
    }
  }

  return (
    <Card title="Nyt opslag">
      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {MARKET_KINDS.map((k) => (
            <button key={k.key} type="button" className={`btn btn--sm ${kind === k.key ? 'btn--primary' : ''}`} onClick={() => setKind(k.key)}>
              {k.label}
            </button>
          ))}
        </div>
        <label className="field">
          <span className="field__label">Overskrift *</span>
          <input maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="F.eks. Trailer 750 kg" />
        </label>
        <label className="field">
          <span className="field__label">Pris</span>
          <input maxLength={40} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="F.eks. 1.500 kr. eller Byd" />
        </label>
        <label className="field">
          <span className="field__label">Beskrivelse</span>
          <textarea rows={3} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Billeder (højst 3)</span>
          <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 3))} />
        </label>
        <div className="tiny muted">Opslaget står i 30 dage. Du kan forlænge det eller trykke "Solgt" / "Afslut".</div>
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={!title.trim() || create.isPending} onClick={() => void send()}>
            {create.isPending ? 'Gemmer ...' : 'Opret opslaget'}
          </button>
          <button className="btn" onClick={onClose}>Annullér</button>
        </div>
      </div>
    </Card>
  )
}

// --- Ugens faglige tip ---

/** Fanen "Faglige tip": ugens tip øverst med "Jeg har læst det", arkivet nedenunder. Kontoret skriver. */
export function TipsTab() {
  const { data, isLoading, error } = useIntranetTips()
  const { isIntranetAdmin } = useAuth()
  const [editing, setEditing] = useState<IntranetTip | 'new' | null>(null)

  useCreateRequest(['tip'], () => setEditing('new'))

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const current = data?.find((t) => t.isThisWeek)
  const archive = (data ?? []).filter((t) => t !== current)

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isIntranetAdmin && editing === null && (
        <div>
          <button className="btn btn--primary" onClick={() => setEditing('new')}>💡 Skriv ugens tip</button>
        </div>
      )}
      {editing !== null && <TipForm tip={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}

      {current ? (
        <TipCard tip={current} highlight onEdit={isIntranetAdmin ? () => setEditing(current) : undefined} />
      ) : (
        <EmptyState>Der er ikke skrevet et tip i denne uge endnu.</EmptyState>
      )}

      {archive.length > 0 && (
        <Card title="Tidligere tip">
          <div className="stack" style={{ gap: 2 }}>
            {archive.map((tip) => <ArchivedTip key={tip.id} tip={tip} onEdit={isIntranetAdmin ? () => setEditing(tip) : undefined} />)}
          </div>
        </Card>
      )}
    </div>
  )
}

function TipCard({ tip, highlight, onEdit }: { tip: IntranetTip; highlight?: boolean; onEdit?: () => void }) {
  const markRead = useMarkIntranetTipRead()
  const remove = useDeleteIntranetTip()

  return (
    <Card title={highlight ? `💡 Ugens tip: ${tip.title}` : tip.title}>
      <div className="stack" style={{ gap: 10 }}>
        {tip.photoUrl && <Photo url={tip.photoUrl} size={220} />}
        <div style={{ whiteSpace: 'pre-wrap' }}>{tip.body}</div>
        <div className="tiny muted">
          {tip.createdBy?.displayName ?? 'Kontoret'} · {formatDate(tip.createdAt)}
          {onEdit && ` · ${tip.readCount} har læst det`}
        </div>
        <div className="btn-group">
          {tip.isRead ? (
            <span className="small" style={{ color: '#16A34A' }}>✓ Du har læst det</span>
          ) : (
            <button className="btn btn--primary btn--sm" disabled={markRead.isPending} onClick={() => markRead.mutate(tip.id)}>
              Jeg har læst det
            </button>
          )}
          {onEdit && <button className="btn btn--sm" onClick={onEdit}>Ret</button>}
          {onEdit && (
            <button className="btn btn--sm" disabled={remove.isPending} onClick={() => { if (window.confirm('Slet tippet?')) remove.mutate(tip.id) }}>
              Slet
            </button>
          )}
        </div>
      </div>
    </Card>
  )
}

function ArchivedTip({ tip, onEdit }: { tip: IntranetTip; onEdit?: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="device-row">
      <button type="button" className="row row--between" style={{ width: '100%', background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }} onClick={() => setOpen((v) => !v)}>
        <span className="small"><strong>{tip.title}</strong></span>
        <span className="tiny muted">{formatDate(tip.createdAt)} {open ? '▴' : '▾'}</span>
      </button>
      {open && <div style={{ marginTop: 8 }}><TipCard tip={tip} onEdit={onEdit} /></div>}
    </div>
  )
}

function TipForm({ tip, onClose }: { tip: IntranetTip | null; onClose: () => void }) {
  const save = useSaveIntranetTip()
  const [title, setTitle] = useState(tip?.title ?? '')
  const [body, setBody] = useState(tip?.body ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [removePhoto, setRemovePhoto] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send() {
    setError(null)
    try {
      await save.mutateAsync({
        id: tip?.id,
        title: title.trim(),
        body: body.trim(),
        photo: file ? await prepareDocumentFile(file) : null,
        removePhoto
      })
      onClose()
    } catch (err) {
      setError(errorText(err, 'Tippet kunne ikke gemmes.'))
    }
  }

  return (
    <Card title={tip ? 'Ret tippet' : '💡 Skriv ugens tip'}>
      <div className="stack" style={{ gap: 10 }}>
        <label className="field">
          <span className="field__label">Overskrift *</span>
          <input maxLength={150} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="F.eks. Sådan tester du en RCD rigtigt" />
        </label>
        <label className="field">
          <span className="field__label">Tippet *</span>
          <textarea rows={6} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Billede</span>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        {tip?.photoUrl && !file && (
          <label className="row small" style={{ gap: 8 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={removePhoto} onChange={(e) => setRemovePhoto(e.target.checked)} />
            Fjern billedet
          </label>
        )}
        {!tip && <div className="tiny muted">Alle får en besked, og tippet står på TV'et resten af ugen.</div>}
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={!title.trim() || !body.trim() || save.isPending} onClick={() => void send()}>
            {save.isPending ? 'Gemmer ...' : tip ? 'Gem' : 'Udgiv tippet'}
          </button>
          <button className="btn" onClick={onClose}>Annullér</button>
        </div>
      </div>
    </Card>
  )
}
